/** @jsxImportSource @emotion/react */

import {
  EuiContextMenuItemIcon,
  EuiContextMenuPanel,
  EuiHorizontalRule,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { useMenuBarStyles } from "./useMenuBarStyles";
import { UserPermission } from "types/UserAccessLevels";
import { ContextMenuItemPermissioned } from "components/ContextMenuItemPermissioned/ContextMenuItemPermissioned";
import { isDefined } from "utils/isDefined";

interface MenuBarMenuItem {
  type: "item";
  disabled?: boolean;
  icon: EuiContextMenuItemIcon;
  label: string;
  onClick: () => void;
  requiredPermission: UserPermission;
  limitToType?: "data" | "analysis";
}

interface MenuBarMenuSeparator {
  key: string;
  type: "separator";
}

interface MenuBarMenuCustomItem {
  type: "custom";
  content: React.ReactNode;
}

export interface MenuBarMenuProps {
  enableHoverToOpen: boolean;
  isOpen: boolean;
  items: (MenuBarMenuItem | MenuBarMenuSeparator | MenuBarMenuCustomItem)[];
  label: string;
  onClose: () => void;
  onOpen: () => void;
  tableType: "data" | "analysis";
}

export const MenuBarMenu = ({
  enableHoverToOpen,
  isOpen,
  items,
  label,
  onClose,
  onOpen,
  tableType,
}: MenuBarMenuProps) => {
  const { menuBarMenuButtonStyle } = useMenuBarStyles();

  const handleMouseEnter = () => {
    if (enableHoverToOpen) {
      onOpen();
    }
  };

  const toggleOpen = () => (isOpen ? onClose() : onOpen());

  const handleClickItem = (item: MenuBarMenuItem) => {
    onClose();
    item.onClick();
  };

  // filter items based on the tableType
  const filteredItems = items.filter((item) => {
    if (item.type === "item") {
      if (isDefined(item.limitToType)) {
        return item.limitToType === tableType;
      }
      return true;
    }
    return true;
  });

  return (
    <EuiPopover
      anchorPosition="downLeft"
      buffer={0}
      button={
        <button
          css={menuBarMenuButtonStyle(isOpen)}
          onClick={toggleOpen}
          onMouseEnter={handleMouseEnter}
        >
          {label}
        </button>
      }
      closePopover={onClose}
      hasArrow={false}
      isOpen={isOpen}
      panelPaddingSize="none"
    >
      <EuiContextMenuPanel
        items={filteredItems.map((item) => {
          if (item.type === "item") {
            return (
              <ContextMenuItemPermissioned
                key={item.label}
                disabled={item.disabled}
                icon={item.icon}
                onClick={() => handleClickItem(item)}
                style={{ padding: `6px 8px` }}
                requiredPermission={item.requiredPermission}
              >
                {item.label}
              </ContextMenuItemPermissioned>
            );
          } else if (item.type === "separator") {
            return <EuiHorizontalRule key={item.key} margin="none" />;
          } else if (item.type === "custom") return item.content;
          return null;
        })}
        size="s"
      />
    </EuiPopover>
  );
};
